// WelcomeFrame: the welcome page's first screen in a view other than the
// flow board (welcome/views.js): the page's search box, the view under it,
// and under the view the line saying which sessions it is drawn from, the
// views named at its end. The view gets the room between them and what the
// board gets: the sessions the search reaches and where review left each,
// the counts over all of them, and the search to narrow.
//
// What a view (`view.Body`) is given:
// - `facts`: the sessions (lib/sessionFacts.js), `stale` while the next read
//   is under way (the frame dims the view meanwhile);
// - `metrics`: the counts over all the search reaches (`metrics.data`:
//   sessions, requests, endpoints); `reached`: past the sample's cap, how
//   many of them review left at each place, else null;
// - `range` and `bounds` ({ from, to } in ms): the page's range;
// - `width` and `height`: the room it has, which it fills and keeps to;
// - `query`, `onPick(clause)`, `onPickAll(clauses)`: the search, and a click
//   that narrows it to a clause (or several), or widens it back out of what
//   it holds already; `clauseFor(def, value)`: a board step's clause;
// - `hues`: each agent's colour; `groups`: whether the directory has groups;
// - `preview`: the value pointed at in the search box's finder ({ of, value,
//   def }), or null;
// - `onShowAll(part)`: goes down the page to a section (Triage, People,
//   Agents...), for the rest of what the view folds.
import { Suspense, useCallback, useState } from "react";
import BoardSearch, { SEARCH_H } from "../BoardSearch.jsx";
import ErrorBoundary from "../ErrorBoundary.jsx";
import { FINDS, FINDS_NO_GROUPS, LIT_FILL, SAMPLE_H, SampleLine, UNDER_SEARCH, useClauseFor } from "../FlowBoard.jsx";
import { withoutClause } from "../SearchPalette.jsx";
import { appendClause, holdsClause, setWorkspaceSearch, useWorkspaceSearch } from "../../lib/workspaceSearch.js";
import { BodyState } from "./parts.jsx";

const READING = { status: "loading", sessions: [] };
// Keyboard focus on anything a view draws, as pointing is: the fill a shade
// up, framed a shade up, so no browser ring.
const VIEW_FOCUS = "[&_button]:outline-none [&_button:focus-visible]:bg-[var(--row-hover)] [&_button:focus-visible]:[outline:1px_solid_color-mix(in_srgb,var(--muted)_55%,transparent)] [&_button:focus-visible]:[outline-offset:-1px]";

export default function WelcomeFrame({ view, facts, metrics, reached = null, range, bounds, width, height, notice = null, onPick, onStart, startLabel, startHint, searchHost = null, hues, groups = true, onShowAll, aside = null }) {
  const clauseFor = useClauseFor(facts);
  const [query] = useWorkspaceSearch();
  const [preview, setPreview] = useState(null);
  // A click that stands for several things at once (a cell of a person and
  // a kind of work): the search holding them all already widens back out of
  // them all; else it narrows to those it does not hold.
  const onPickAll = useCallback((clauses) => setWorkspaceSearch((t) => {
    const list = clauses.filter(Boolean);
    if (!list.length) return t;
    if (list.every((c) => holdsClause(t, c))) return list.reduce((acc, c) => withoutClause(acc, c), t);
    return list.reduce((acc, c) => appendClause(acc, c), t);
  }), []);
  const top = SEARCH_H + UNDER_SEARCH;
  const bodyH = Math.max(240, height - top - SAMPLE_H);
  const finderWidth = Math.min(width, Math.max(480, Math.min(640, width * 0.4)));
  const finderHeight = Math.max(240, Math.min(560, height - SEARCH_H - 24));
  const Body = view.Body;
  return (
    <div className="relative" style={{ width, height }} data-welcome-view={view.id}>
      <BoardSearch facts={facts} reached={reached} kinds={groups ? FINDS : FINDS_NO_GROUPS} notice={notice} clauseFor={clauseFor} metricsData={metrics.data} width={width} range={range} finderWidth={finderWidth} finderHeight={finderHeight} onPreview={setPreview} onStart={onStart} startLabel={startLabel} startHint={startHint} host={searchHost} />
      <div className={`absolute left-0 transition-opacity duration-200 ${VIEW_FOCUS}`} style={{ top, width, height: bodyH, opacity: facts.stale ? 0.45 : 1, "--row-hover": LIT_FILL }} data-welcome-body="">
        {/* A view that fails to draw says so in its own room, the page around it kept. */}
        <ErrorBoundary key={view.id} fallback={<p className="m-0 text-[12px] text-[color:var(--muted)]" role="alert">This view could not be drawn.</p>}>
          <Suspense fallback={<BodyState facts={READING} />}>
            <Body facts={facts} metrics={metrics} reached={reached} range={range} bounds={bounds} width={width} height={bodyH} query={query} onPick={onPick} onPickAll={onPickAll} clauseFor={clauseFor} hues={hues} groups={groups} preview={preview} onShowAll={onShowAll} />
          </Suspense>
        </ErrorBoundary>
      </div>
      <SampleLine facts={facts} top={top + bodyH + 6} width={width} aside={aside} />
    </div>
  );
}
